import React from 'react';
import { motion } from 'framer-motion';
import { UserRoundIcon } from 'lucide-react';
import { CaseSlideShell } from './CaseSlideShell';
const CX = 300;
const CY = 200;
const CALLED = Array.from({
  length: 14
}, (_, i) => {
  const angle = i / 14 * Math.PI * 2 + 0.3;
  const radius = 46 + i % 3 * 13;
  return {
    x: CX + Math.cos(angle) * radius * 1.3,
    y: CY + Math.sin(angle) * radius
  };
});
const NON_CALLED = Array.from({
  length: 40
}, (_, i) => {
  const angle = i / 40 * Math.PI * 2 + i % 2 * 0.08;
  const radius = 132 + i * 37 % 5 * 11;
  return {
    x: CX + Math.cos(angle) * radius * 1.45,
    y: CY + Math.sin(angle) * radius * 0.95,
    linked: i % 8 === 0
  };
});
export function CaseChallenge() {
  return <CaseSlideShell step="01" eyebrow="The challenge" headline={<>
          What about the HCPs{' '}
          <span className="text-[var(--brand)]" data-id="element-170" data-mp="rjq9wgfawn2c">the field force doesn’t reach?</span>
        </>} intro="Field resources naturally prioritise a core group of HCPs. That leaves a significant non-called audience with limited BI interaction." data-id="element-169" data-mp="27uanq4m223eo">
      <div className="flex min-h-0 flex-1 items-center gap-[clamp(1rem,3vw,3rem)]" data-id="element-171" data-mp="1sayyexaes8sh">
        <motion.figure initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2,
        duration: 0.6
      }} className="relative flex h-full min-h-0 flex-1 items-center justify-center" data-id="element-172" data-mp="277g8zmarxjdz">
          <svg viewBox="0 0 600 400" className="h-full max-h-full w-full" role="img" aria-label="Diagram: a small core of called HCPs tightly connected to the field force, surrounded by a larger, lightly connected non-called audience" data-id="element-173" data-mp="2b8rq13fl0wak">
            <ellipse cx={CX} cy={CY} rx={282} ry={188} fill="none" stroke="var(--brand)" strokeOpacity={0.25} strokeDasharray="4 7" strokeWidth={1.5} data-id="element-174" data-mp="2l0xiee9qxfqa" />
            <ellipse cx={CX} cy={CY} rx={112} ry={90} fill="var(--brand)" fillOpacity={0.08} stroke="var(--brand)" strokeOpacity={0.5} strokeWidth={1.5} data-id="element-175" data-mp="2uy25cgbf1f85" />
            {NON_CALLED.filter(d => d.linked).map(d => <line key={`l-${d.x}`} x1={CX} y1={CY} x2={d.x} y2={d.y} stroke="var(--brand)" strokeOpacity={0.25} strokeDasharray="3 6" strokeWidth={1.2} data-id="element-176" data-mp="36zk0poo62m2y" />)}
            {CALLED.map(d => <line key={`c-${d.x}`} x1={CX} y1={CY} x2={d.x} y2={d.y} stroke="var(--brand)" strokeOpacity={0.55} strokeWidth={1.6} data-id="element-177" data-mp="3gq69ayxgucc5" />)}
            {NON_CALLED.map((d, i) => <motion.circle key={`n-${i}`} cx={d.x} cy={d.y} r={7} fill="var(--muted)" initial={{
            opacity: 0
          }} animate={{
            opacity: 0.32
          }} transition={{
            delay: 0.5 + i * 0.015,
            duration: 0.3
          }} data-id="element-178" data-mp="3pnyom336tjzj" data-mpi={i} />)}
            {CALLED.map((d, i) => <motion.circle key={`d-${i}`} cx={d.x} cy={d.y} r={8} fill="var(--brand)" initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3 + i * 0.03,
            duration: 0.3
          }} data-id="element-179" data-mp="25ahniavy7jcx" data-mpi={i} />)}
            <circle cx={CX} cy={CY} r={24} fill="var(--brand)" data-id="element-180" data-mp="8nyi3wwc4w7n" />
          </svg>
          <span className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center" aria-hidden="true" data-id="element-181" data-mp="15d65lagilfe7">
            <UserRoundIcon className="h-[clamp(1rem,1.6vw,1.5rem)] w-[clamp(1rem,1.6vw,1.5rem)] text-white" data-id="element-182" data-mp="11cm7a3k7g5qh" />
          </span>
        </motion.figure>

        <motion.ul initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.45,
        duration: 0.5
      }} className="flex w-[30%] flex-shrink-0 flex-col gap-[clamp(0.8rem,1.6vw,1.5rem)]" data-id="element-183" data-mp="re6ii78nh8k5">
          <li className="flex items-start gap-3" data-id="element-184" data-mp="3c5yvxsoknvt6">
            <span className="mt-1 h-4 w-4 flex-shrink-0 rounded-full bg-[var(--brand)]" aria-hidden="true" data-id="element-185" data-mp="13uxpznwx8lw3" />
            <span data-id="element-186" data-mp="18mxwjz7hyd1n">
              <span className="block text-[clamp(1rem,1.6vw,1.5rem)] font-extrabold tracking-[-0.02em] text-[var(--fg)]" data-id="element-187" data-mp="18nh4cn3fnl1y">Called HCPs</span>
              <span className="block text-[clamp(0.78rem,1.05vw,0.98rem)] leading-snug text-[var(--muted)]" data-id="element-188" data-mp="ztm15klvg4oy">A prioritised core with regular field-force contact.</span>
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-189" data-mp="1rks1a11le2e8">
            <span className="mt-1 h-4 w-4 flex-shrink-0 rounded-full bg-[var(--muted)]/35" aria-hidden="true" data-id="element-190" data-mp="1rjgxw2c26wnn" />
            <span data-id="element-191" data-mp="1dkr4awog7f41">
              <span className="block text-[clamp(1rem,1.6vw,1.5rem)] font-extrabold tracking-[-0.02em] text-[var(--fg)]" data-id="element-192" data-mp="1dnd94ojyfgik">Non-called HCPs</span>
              <span className="block text-[clamp(0.78rem,1.05vw,0.98rem)] leading-snug text-[var(--muted)]" data-id="element-193" data-mp="25kingcprki0">A larger audience, only lightly connected to BI.</span>
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-194" data-mp="3vq0nn9zugv8d">
            <span className="mt-2 h-0 w-4 flex-shrink-0 border-t-2 border-dashed border-[var(--brand)]/50" aria-hidden="true" data-id="element-195" data-mp="3vswiuqkb55t4" />
            <span className="block text-[clamp(0.78rem,1.05vw,0.98rem)] leading-snug text-[var(--muted)]" data-id="element-196" data-mp="8lu5bhjqv6ow">Occasional, indirect touchpoints.</span>
          </li>
        </motion.ul>
      </div>
    </CaseSlideShell>;
}